import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useSession } from "@core/session/SessionProvider";
import { tokens } from "@core/theme/tokens";
import { Icon, StarMark } from "@core/icons";
import { NAV } from "./nav";
import { shellStyles } from "./shellStyles";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

// Extraído de admin.jsx:3786-3820 (components.md §3.1: "no está extraída
// como componente... al implementar el shell de SPEC-201 hay que
// extraerla"). Una entrada solo se muestra si el perfil tiene permiso de ver
// sobre su módulo (SPEC-201 §2, criterio de aceptación 1).

// "Cerrar sesión" vivía en un Header separado (SPEC-201) que duplicaba la
// identidad ya mostrada acá abajo — ningún baseline de
// context/prototype/screenshots/baselines/portal-*.png tiene esa barra
// superior (admin.jsx:3789-3853 no la incluye). Se movió al pie de la
// barra lateral, oculta por defecto: aparece en :hover/:focus-within (mouse
// y teclado) o con `identityOpen` como alternativa explícita para quien no
// tiene puntero con hover (ej. touch) — no depender solo de :hover para que
// la acción sea alcanzable (fix a pedido humano, run 2026-08-24 pasada 3).
// El botón vive en su propia fila (.sidebar-identity__actions), no en la del
// avatar: oculto solo con `opacity` seguía en el flujo flex de esa fila y le
// reservaba ancho a la columna `flex: 1` del nombre, que terminaba envolviendo
// palabra por palabra (revisión en vivo 2026-09-22, punto #1). La fila colapsa
// a alto 0 sin salir del DOM ni del orden de tabulación, así que el foco de
// teclado la sigue expandiendo por :focus-within (base.css).
export function Sidebar() {
  const { hasPermission, session, signOut } = useSession();
  const navigate = useNavigate();
  const [identityOpen, setIdentityOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const visible = NAV.filter(
    (entry) =>
      hasPermission(entry.permissionModule, "canView") &&
      (!entry.adminOnly || session?.user.isAdministrator === true),
  );
  const edges = useScrollEdges(navRef, visible.length);

  // signOut() solo limpia el token y la caché de sesión — igual que ya hace
  // IdleSessionGuard al expirar por inactividad, la navegación explícita a
  // /access le toca a quien dispara el cierre, no queda implícita (lógica
  // heredada de Header.tsx:11-17, ya eliminado).
  async function handleSignOut() {
    await signOut();
    navigate("/access", { replace: true });
  }

  return (
    <aside
      style={{
        width: shellStyles.aside.width,
        flex: "none",
        borderRight: `1px solid ${tokens.surface.line}`,
        padding: shellStyles.aside.padding,
        position: "sticky",
        top: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: shellStyles.logo.gap,
          padding: shellStyles.logo.padding,
          flex: "none",
        }}
      >
        <StarMark size={32} ring stroke={1} />
        <div>
          <div
            style={{
              font: shellStyles.title,
              letterSpacing: shellStyles.titleLetterSpacing,
              textTransform: "uppercase",
              color: tokens.text.secondary,
            }}
          >
            Star Circle
          </div>
          <div
            style={{
              font: shellStyles.subtitle,
              color: tokens.text.disabled,
              marginTop: shellStyles.subtitleMarginTop,
            }}
          >
            Portal administrativo
          </div>
        </div>
      </div>

      <nav
        ref={navRef}
        aria-label="Navegación principal"
        className="sidebar-nav"
        data-fade-top={edges.top ? "" : undefined}
        data-fade-bottom={edges.bottom ? "" : undefined}
      >
        {visible.map((entry) => (
          <NavLink
            key={entry.key}
            to={entry.path}
            style={({ isActive }) => ({
              display: "flex",
              alignItems: "center",
              gap: shellStyles.navLink.gap,
              width: "100%",
              textAlign: "left",
              padding: shellStyles.navLink.padding,
              borderRadius: shellStyles.navLink.borderRadius,
              marginBottom: shellStyles.navLink.marginBottom,
              textDecoration: "none",
              background: isActive ? tokens.surface.surface2 : "transparent",
              border: `1px solid ${isActive ? tokens.surface.line2 : "transparent"}`,
              color: isActive ? tokens.text.primary : tokens.text.tertiary,
              font: shellStyles.navLink.font,
            })}
          >
            <Icon name={entry.icon} size={17} stroke={1.5} />
            {entry.label}
          </NavLink>
        ))}
      </nav>

      {session ? (
        <div
          className={`sidebar-identity${identityOpen ? " sidebar-identity--open" : ""}`}
          onClick={() => setIdentityOpen((open) => !open)}
          style={{
            flex: "none",
            marginTop: 12,
            paddingTop: shellStyles.identity.paddingTop,
            padding: shellStyles.identity.padding,
            border: `1px solid ${tokens.surface.line}`,
            borderRadius: shellStyles.identity.borderRadius,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: shellStyles.identity.gap,
            }}
          >
            <div
              className="av"
              style={{
                width: shellStyles.avatar.size,
                height: shellStyles.avatar.size,
                fontSize: shellStyles.avatar.fontSize,
              }}
            >
              {initials(session.user.name)}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{ font: shellStyles.name, color: tokens.text.primary }}
              >
                {session.user.name}
              </div>
              <div
                style={{
                  font: shellStyles.profile,
                  color: tokens.text.disabled,
                }}
              >
                {session.user.profile}
              </div>
            </div>
          </div>
          <div className="sidebar-identity__actions">
            <button
              type="button"
              className="chip"
              onClick={(event) => {
                event.stopPropagation();
                void handleSignOut();
              }}
              style={{
                font: shellStyles.signOut,
                width: "100%",
                justifyContent: "center",
                marginTop: shellStyles.identity.gap,
              }}
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

// Revisión en vivo 2026-09-22: marca y pie de identidad quedan fijos; solo
// la lista de opciones se desplaza, sin barra visible. El degradado de
// `.sidebar-nav` (base.css) aparece arriba o abajo únicamente cuando queda
// contenido oculto en esa dirección: sin desborde no hay degradado, así que
// no se atenúa la primera ni la última opción cuando todo entra.
// `itemCount` re-mide cuando cambian las opciones visibles (los permisos
// llegan después del primer render): el `ResizeObserver` solo ve el tamaño de
// la caja de `<nav>`, que no cambia cuando crece su contenido.
function useScrollEdges(
  ref: React.RefObject<HTMLElement | null>,
  itemCount: number,
) {
  const [edges, setEdges] = useState({ top: false, bottom: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const top = el.scrollTop > 1;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setEdges((prev) =>
        prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
      );
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(el);
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [ref, itemCount]);
  return edges;
}
